/**
 * Home's dashboard layout (the slots are the page's; what goes in them is home.tsx's). Wide screens
 * (xl, 1280 px and up) use twelve columns:
 *
 *   guide (only while setup is unfinished)                          12
 *   KPI strip                                                        12
 *   trend chart                                   8 | attention, right now          4
 *   most played                         6 | money or devices                         6
 *   recent activity                               8 | changes, storage, folded guide 4
 *
 * Cards beside each other share their row's height: each slot stretches its card, and the last card
 * of a stacked column takes what is left. Below xl it is one column in priority order: unfinished
 * guide, attention (only when something needs it), KPIs, trend, right now, most played, money or
 * devices, changes, activity, storage, folded guide. The stacked columns are a box only on wide
 * screens (`display: contents` below), so their cards join that one column in their own order.
 */

type Node = React.ReactNode;

export interface DashboardSlots {
  /** The setup guide while it is unfinished: first, full width. */
  guide?: Node;
  attention: Node;
  /** Nothing needs attention: below xl the all-clear line is left out (it stays in the wide column). */
  attentionQuiet?: boolean;
  kpis: Node;
  trend: Node;
  rightNow: Node;
  top: Node;
  /** Money for a brand that sells, devices for one that does not. */
  money: Node;
  changes: Node;
  activity: Node;
  storage: Node;
  /** The finished (folded) setup guide: last in the activity column. */
  foldedGuide?: Node;
}

export function DashboardGrid(slots: DashboardSlots) {
  return (
    <div className="flex flex-col gap-3 xl:grid xl:grid-cols-12">
      {slots.guide ? <Slot className="max-xl:order-1 xl:col-span-12">{slots.guide}</Slot> : null}
      <Slot className="max-xl:order-3 xl:col-span-12">{slots.kpis}</Slot>
      <Slot className="max-xl:order-4 xl:col-span-8">{slots.trend}</Slot>
      <Column>
        <Slot className={`max-xl:order-2 ${slots.attentionQuiet ? "max-xl:hidden" : ""}`}>{slots.attention}</Slot>
        <Slot className="max-xl:order-5 xl:grow">{slots.rightNow}</Slot>
      </Column>
      <Slot className="max-xl:order-6 xl:col-span-6">{slots.top}</Slot>
      <Slot className="max-xl:order-7 xl:col-span-6">{slots.money}</Slot>
      <Slot className="max-xl:order-9 xl:col-span-8">{slots.activity}</Slot>
      <Column>
        <Slot className={`max-xl:order-8 ${slots.foldedGuide ? "" : "xl:grow"}`}>{slots.changes}</Slot>
        <Slot className={`max-xl:order-10 ${slots.foldedGuide ? "" : "xl:grow"}`}>{slots.storage}</Slot>
        {slots.foldedGuide ? <Slot className="max-xl:order-11 xl:grow">{slots.foldedGuide}</Slot> : null}
      </Column>
    </div>
  );
}

/** One card's place: the card fills it, so cards side by side end on the same line. */
function Slot({ className, children }: { className: string; children: Node }) {
  return <div className={`flex min-w-0 flex-col [&>*]:grow ${className}`}>{children}</div>;
}

/** Four columns of stacked cards on wide screens; no box of its own below. */
function Column({ children }: { children: Node }) {
  return <div className="max-xl:contents xl:col-span-4 xl:flex xl:min-w-0 xl:flex-col xl:gap-3">{children}</div>;
}
